import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Activity, Eye, Users, Globe, Smartphone, Monitor, Tablet } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const RANGE_OPTS = [
  { label: "7 days",  days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
];

const fmtDate = (iso) => {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  } catch { return iso; }
};

const DeviceIcon = ({ d }) =>
  d === "mobile" ? <Smartphone size={12} /> :
  d === "tablet" ? <Tablet size={12} /> :
  d === "desktop" ? <Monitor size={12} /> :
  <Globe size={12} />;

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = (d) => {
    setLoading(true);
    api.get("/admin/analytics/overview", { params: { days: d } })
      .then(r => setData(r.data))
      .catch(() => toast.error("Could not load analytics"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(days); }, [days]);

  return (
    <div data-testid="admin-analytics-page">
      <Toaster position="top-center" richColors />
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Traffic</div>
          <h1 className="display text-4xl mt-2 flex items-center gap-3">
            <Activity size={26} /> Site analytics.
          </h1>
          <p className="text-sm text-muted2 mt-2 max-w-xl">
            In-house counter. Cookie-free, no third parties. Admin pageviews are excluded from these totals.
          </p>
        </div>
        <div className="inline-flex border border-soft" data-testid="analytics-range">
          {RANGE_OPTS.map(o => (
            <button
              key={o.days}
              onClick={() => setDays(o.days)}
              className={`px-4 py-2 text-xs tracking-widest uppercase transition ${days === o.days ? "bg-charcoal text-cream" : "hover:bg-cream-2"}`}
              style={days === o.days ? { background: "#1C1C19", color: "#F9F8F6" } : {}}
              data-testid={`analytics-range-${o.days}`}
            >{o.label}</button>
          ))}
        </div>
      </div>

      {loading && <div className="mt-12 text-muted2">Loading…</div>}

      {!loading && data && (
        <>
          {/* Top-line stat tiles */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatTile label="Today"        value={data.totals.today}       accent />
            <StatTile label="Yesterday"    value={data.totals.yesterday} />
            <StatTile label="Last 7 days"  value={data.totals.last_7_days} />
            <StatTile label="Last 30 days" value={data.totals.last_30_days} />
          </div>

          {/* Daily chart */}
          <section className="mt-12 border border-soft p-6 bg-white" data-testid="analytics-chart">
            <div className="flex items-baseline justify-between">
              <div className="font-serif text-2xl">Daily visitors — last {data.range.days} days</div>
              <div className="text-xs text-muted2">
                {data.totals.window.visitors.toLocaleString("en-IN")} unique · {data.totals.window.pageviews.toLocaleString("en-IN")} pageviews
              </div>
            </div>
            <div className="mt-6 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.series} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEECE6" />
                  <XAxis dataKey="day" tickFormatter={fmtDate} stroke="#8A867E" fontSize={11} tickMargin={8} />
                  <YAxis allowDecimals={false} stroke="#8A867E" fontSize={11} width={30} />
                  <Tooltip
                    contentStyle={{ background: "#F9F8F6", border: "1px solid #EEECE6", fontFamily: "Outfit, sans-serif", fontSize: 12 }}
                    labelFormatter={fmtDate}
                  />
                  <Line type="monotone" dataKey="visitors" stroke="#C05C42" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} name="Unique visitors" />
                  <Line type="monotone" dataKey="pageviews" stroke="#4A5D4E" strokeWidth={1.5} strokeDasharray="4 4" dot={false} name="Pageviews" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>

          {/* Split panels */}
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <Panel title="Top pages" icon={<Eye size={14} />} rows={data.top_pages} nameKey="path" testId="analytics-pages" />
            <Panel title="Top referrers" icon={<Globe size={14} />} rows={data.top_referrers} nameKey="source" testId="analytics-refs" />
            <Panel title="Devices" icon={<Smartphone size={14} />} rows={data.devices} nameKey="device" testId="analytics-devices"
              rowIcon={(row) => <DeviceIcon d={row.device} />} />
          </div>

          <p className="mt-10 text-xs text-muted2 leading-relaxed max-w-2xl">
            How we count: each pageview is bucketed by day (IST) + a one-way hash of IP &amp; browser, so one person browsing multiple pages counts as one visitor for that day. Obvious bots (Googlebot, crawlers, scrapers) are excluded on the server. No cookies, no third-party trackers.
          </p>
        </>
      )}
    </div>
  );
}

function StatTile({ label, value, accent = false }) {
  return (
    <div className={`p-5 border ${accent ? "border-terra" : "border-soft"} bg-white`} data-testid={`stat-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="overline">{label}</div>
      <div className="font-serif text-4xl mt-2 flex items-baseline gap-3">
        <span style={accent ? { color: "#C05C42" } : {}}>{value.visitors.toLocaleString("en-IN")}</span>
        <span className="text-xs text-muted2 tracking-widest uppercase">visitors</span>
      </div>
      <div className="text-xs text-muted2 mt-1">{value.pageviews.toLocaleString("en-IN")} pageviews</div>
    </div>
  );
}

function Panel({ title, icon, rows, nameKey, testId, rowIcon }) {
  const total = rows.reduce((s, r) => s + (r.count || 0), 0) || 1;
  return (
    <section className="border border-soft p-6 bg-white" data-testid={testId}>
      <div className="overline flex items-center gap-2">{icon} {title}</div>
      {rows.length === 0 && <div className="mt-4 text-sm text-muted2">No data yet.</div>}
      <ul className="mt-4 space-y-3">
        {rows.slice(0, 10).map((r, i) => {
          const pct = Math.round((r.count / total) * 100);
          return (
            <li key={`${r[nameKey]}-${i}`} className="text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="truncate flex items-center gap-2" title={r[nameKey]}>
                  {rowIcon && rowIcon(r)}
                  <span className="truncate">{r[nameKey] || "—"}</span>
                </span>
                <span className="text-xs text-muted2 whitespace-nowrap">{r.count} · {pct}%</span>
              </div>
              <div className="mt-1 h-1 bg-cream-2 overflow-hidden">
                <div className="h-full" style={{ width: `${pct}%`, background: "#C05C42" }} />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
